import { Observable, Subject } from 'rxjs';

import { DriveItem } from '@/drive/entities/DriveItem/DriveItem';
import { DriveContainerId, DriveItemId } from '@/drive/entities/DriveItemId';

import {
  AddSuccessItem,
  DriveItemPatch,
  DriveModifierEvent,
  DriveModifierEventType,
  OptimisticUpdatePhase,
} from './DriveModifierStore.types';

export class DriveModifierEventEmitter {
  private eventSubject = new Subject<DriveModifierEvent>();

  get event$(): Observable<DriveModifierEvent> {
    return this.eventSubject.asObservable();
  }

  private emit(event: DriveModifierEvent) {
    this.eventSubject.next(event);
  }

  addApply(
    temporaryItems: DriveItem[],
    parentId: DriveContainerId,
    precedingNodeIdByTempId = new Map<DriveItemId, DriveItemId>(),
  ) {
    this.emit({
      type: DriveModifierEventType.Add,
      phase: OptimisticUpdatePhase.Apply,
      temporaryItems,
      parentId,
      precedingNodeIdByTempId,
    });
  }

  updateApply(patch: DriveItemPatch) {
    this.emit({
      type: DriveModifierEventType.Update,
      phase: OptimisticUpdatePhase.Apply,
      patch,
    });
  }

  updateSuccess(patch: DriveItemPatch) {
    this.emit({
      type: DriveModifierEventType.Update,
      phase: OptimisticUpdatePhase.Success,
      patch,
    });
  }

  deleteApply(item: DriveItem) {
    this.emit({
      type: DriveModifierEventType.Delete,
      phase: OptimisticUpdatePhase.Apply,
      item,
    });
  }

  deleteSuccess(item: DriveItem) {
    this.emit({
      type: DriveModifierEventType.Delete,
      phase: OptimisticUpdatePhase.Success,
      item,
    });
  }

  addSuccess(items: AddSuccessItem[], parentId: DriveContainerId, hasOptimisticData: boolean) {
    this.emit({
      type: DriveModifierEventType.Add,
      phase: OptimisticUpdatePhase.Success,
      hasOptimisticData,
      items,
      parentId,
    });
  }

  moveApply(item: DriveItem, parentId: DriveContainerId, previousParentId: DriveContainerId) {
    this.emit({
      type: DriveModifierEventType.Move,
      phase: OptimisticUpdatePhase.Apply,
      item,
      parentId,
      previousParentId,
    });
  }

  moveSuccess(backendItem: DriveItem, parentId: DriveContainerId, previousParentId: DriveContainerId) {
    this.emit({
      type: DriveModifierEventType.Move,
      phase: OptimisticUpdatePhase.Success,
      backendItem,
      parentId,
      previousParentId,
    });
  }

  reorderApply(parentId: DriveContainerId, orderedIds: DriveItemId[]) {
    this.emit({
      type: DriveModifierEventType.Reorder,
      phase: OptimisticUpdatePhase.Apply,
      parentId,
      orderedIds,
    });
  }

  accessChangeSuccess(itemId: DriveItemId) {
    this.emit({
      type: DriveModifierEventType.AccessChange,
      phase: OptimisticUpdatePhase.Success,
      itemId,
    });
  }

  error(type: DriveModifierEventType) {
    this.emit({
      type,
      phase: OptimisticUpdatePhase.Error,
    });
  }
}
